/**
 * Admin Dashboard — the founder's morning-coffee operational view.
 *
 * Replaces the broken ComprehensiveAdminPortal dashboard tab. Single backend
 * call to GET /api/admin/dashboard/overview with 60s polling. Reuses the
 * same KpiTile / ChartCard / TrendChart components as the Platform Analytics
 * surface for visual consistency.
 *
 * Layout:
 *   Row 1 — Topline KPIs: Active Trials | Subscribers | MRR | ARR
 *   Row 2 — Conversion KPIs: Trial→Paid | No Churn M1 | Ess→Chat+ | →Pro
 *   Row 3 — Operational: Exercises This Week | Support Tickets
 *   Row 4 — LLM Cost by Tier (30-day multi-line chart)
 *   Row 5 — Projected Monthly LLM Spend (conditional, ≥7 days of data)
 */

import React from 'react';
import {
  Activity,
  ArrowRightLeft,
  CreditCard,
  DollarSign,
  Dumbbell,
  LifeBuoy,
  ShieldCheck,
  TrendingUp,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { adminFetch } from '../../utils/adminApi';
import { KpiTile } from '../admin/analytics/components/KpiTile';
import { ChartCard } from '../admin/analytics/components/ChartCard';
import { TrendChart, type Series } from '../admin/analytics/components/TrendChart';
import { TIER_COLORS, TIER_DISPLAY } from '../admin/analytics/theme';
import { formatCurrency, formatInt, formatPercent } from '../admin/analytics/format';
import { Card, CardContent } from '../../components/ui/card';
import { BranchDriftWidget } from './BranchDriftWidget';

// ── Types ────────────────────────────────────────────────────────────────────

interface DashboardData {
  kpis: {
    active_trials: number;
    subscribers: { basic: number; full: number; extended: number; total: number };
    mrr: number;
    arr: number;
  };
  conversions: {
    trial_to_paid_pct: number | null;
    trial_starts: number;
    trial_to_paid: number;
    no_churn_month1_pct: number | null;
    paid_30d_ago: number;
    cancelled_in_month1: number;
    essentials_to_chat_plus_mtd: number;
    to_pro_mtd: number;
  };
  support: { new_24h: number; unresolved: number };
  exercises_this_week: { workouts: number; mobility: number; total: number };
  signups: { last_7d: number; prev_7d: number; wow_pct: number | null };
  llm_costs: {
    series: Array<{ day: string; trial: number; basic: number; full: number; extended: number }>;
    projection: {
      has_enough_data: boolean;
      weeks_of_data: number;
      total_monthly: number;
      by_tier: { trial: number; basic: number; full: number; extended: number };
    };
  };
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

// ── Chart config ─────────────────────────────────────────────────────────────

const LLM_SERIES: Series[] = [
  { key: 'trial', label: TIER_DISPLAY.trial, color: TIER_COLORS.trial },
  { key: 'basic', label: TIER_DISPLAY.basic, color: TIER_COLORS.basic },
  { key: 'full', label: TIER_DISPLAY.full, color: TIER_COLORS.full },
  { key: 'extended', label: TIER_DISPLAY.extended, color: TIER_COLORS.extended },
];

// ── QueryClient ──────────────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: true, retry: 1 },
  },
});

// ── Public export ────────────────────────────────────────────────────────────

export function AdminDashboard() {
  return (
    <QueryClientProvider client={queryClient}>
      <DashboardInner />
    </QueryClientProvider>
  );
}

// ── Inner component (inside QueryClientProvider) ─────────────────────────────

function DashboardInner() {
  const { data, isLoading, error, refetch } = useQuery<Envelope<DashboardData>>({
    queryKey: ['admin-dashboard-overview'],
    queryFn: () => adminFetch<Envelope<DashboardData>>('/api/admin/dashboard/overview'),
    refetchInterval: 60_000,
  });

  const d = data?.data;
  const kpis = d?.kpis;
  const conv = d?.conversions;
  const support = d?.support;
  const exercises = d?.exercises_this_week;
  const signups = d?.signups;
  const llm = d?.llm_costs;
  const proj = llm?.projection;

  const subscriberHint = kpis
    ? `${formatInt(kpis.subscribers.basic)} Essentials · ${formatInt(kpis.subscribers.full)} Chat+ · ${formatInt(kpis.subscribers.extended)} Pro`
    : undefined;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--text-primary)' }}>Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Live operational overview — refreshes every 60 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-md bg-white px-3 py-1.5" style={{ boxShadow: 'var(--card-drop)' }}>
          <span className="text-[11px] font-semibold text-gray-500">Since launch</span>
          <span className="text-[11px] font-bold text-gray-800">Apr 9, 2026</span>
          <span className="text-[10px] text-gray-400">· testers excluded</span>
        </div>
      </div>

      {/* Row 1 — Topline KPIs */}
      <section>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Platform
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <KpiTile
            label="New Signups (7d)"
            value={formatInt(signups?.last_7d)}
            deltaPct={signups?.wow_pct ?? null}
            hint={signups ? `vs ${formatInt(signups.prev_7d)} prior week` : undefined}
            icon={<UserPlus className="h-3 w-3" />}
            loading={isLoading}
          />
          <KpiTile
            label="Active Trials"
            value={formatInt(kpis?.active_trials)}
            icon={<Users className="h-3 w-3" />}
            loading={isLoading}
          />
          <KpiTile
            label="Active Subscribers"
            value={formatInt(kpis?.subscribers.total)}
            hint={subscriberHint}
            icon={<UserCheck className="h-3 w-3" />}
            loading={isLoading}
          />
          <KpiTile
            label="MRR"
            value={formatCurrency(kpis?.mrr)}
            icon={<DollarSign className="h-3 w-3" />}
            loading={isLoading}
          />
          <KpiTile
            label="ARR"
            value={formatCurrency(kpis?.arr, 0)}
            icon={<TrendingUp className="h-3 w-3" />}
            loading={isLoading}
          />
        </div>
      </section>

      {/* Row 2 — Conversion KPIs */}
      <section>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Conversions
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            label="Trial → Paid"
            value={conv?.trial_to_paid_pct != null ? formatPercent(conv.trial_to_paid_pct) : '—'}
            hint={conv ? `${formatInt(conv.trial_to_paid)} of ${formatInt(conv.trial_starts)} trials` : undefined}
            icon={<CreditCard className="h-3 w-3" />}
            loading={isLoading}
          />
          <KpiTile
            label="No Churn — Month 1"
            value={conv?.no_churn_month1_pct != null ? formatPercent(conv.no_churn_month1_pct) : '—'}
            hint={conv && conv.paid_30d_ago > 0
              ? `${formatInt(conv.paid_30d_ago - conv.cancelled_in_month1)} of ${formatInt(conv.paid_30d_ago)} retained`
              : 'Need 30+ days of paid users'}
            icon={<ShieldCheck className="h-3 w-3" />}
            loading={isLoading}
          />
          <KpiTile
            label="Essentials → Chat+"
            value={formatInt(conv?.essentials_to_chat_plus_mtd)}
            hint="This month"
            icon={<ArrowRightLeft className="h-3 w-3" />}
            loading={isLoading}
          />
          <KpiTile
            label="→ Pro Upgrades"
            value={formatInt(conv?.to_pro_mtd)}
            hint="This month"
            icon={<ArrowRightLeft className="h-3 w-3" />}
            loading={isLoading}
          />
        </div>
      </section>

      {/* Row 3 — Operational */}
      <section>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <KpiTile
            label="Exercises This Week"
            value={formatInt(exercises?.total)}
            hint={exercises ? `${formatInt(exercises.workouts)} workouts + ${formatInt(exercises.mobility)} mobility` : undefined}
            icon={<Dumbbell className="h-3 w-3" />}
            loading={isLoading}
          />
          <KpiTile
            label="Support Tickets"
            value={support ? `${formatInt(support.new_24h)} new` : '—'}
            hint={support ? `${formatInt(support.unresolved)} unresolved total` : undefined}
            icon={<LifeBuoy className="h-3 w-3" />}
            loading={isLoading}
          />
          <BranchDriftWidget />
        </div>
      </section>

      {/* Row 4 — LLM Cost Chart */}
      <ChartCard
        title="LLM Cost by Tier"
        subtitle="Daily spend over the last 30 days — one line per subscription tier"
        loading={isLoading}
        error={error?.message ?? null}
        isEmpty={!llm?.series?.length}
        emptyMessage="No LLM usage data yet. The chart will populate as users interact with Cedar."
        onRetry={() => refetch()}
      >
        <TrendChart
          data={llm?.series ?? []}
          xKey="day"
          type="line"
          height={300}
          series={LLM_SERIES}
          yFormat={(v) => formatCurrency(v, 2)}
          xFormat={formatDayShort}
        />
      </ChartCard>

      {/* Row 5 — Projected Monthly Spend (conditional) */}
      {proj?.has_enough_data && (
        <Card className="bg-white" style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--card-drop)', border: 'none' }}>
          <CardContent>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              {/* Left — total projection */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Projected Monthly LLM Spend
                </p>
                <p className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">
                  {formatCurrency(proj.total_monthly)}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Based on {proj.weeks_of_data} week{proj.weeks_of_data === 1 ? '' : 's'} of data
                  (avg weekly × 4.33)
                </p>
              </div>

              {/* Right — per-tier breakdown */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
                {(
                  [
                    ['trial', proj.by_tier.trial],
                    ['basic', proj.by_tier.basic],
                    ['full', proj.by_tier.full],
                    ['extended', proj.by_tier.extended],
                  ] as const
                ).map(([tier, amount]) => (
                  <div key={tier} className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: TIER_COLORS[tier] }}
                    />
                    <div className="min-w-0">
                      <div className="text-[10.5px] font-semibold text-gray-500">
                        {TIER_DISPLAY[tier]}
                      </div>
                      <div className="text-sm font-bold tabular-nums text-gray-800">
                        {formatCurrency(amount)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDayShort(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
